import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { X, Copy, Check, Gift, Users, Clock, Share2, Twitter, Mail, MessageCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useTheme } from '../contexts/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHARE_TEXT_SHORT = 'Try NeuroWave One — an honest, science-backed brainwave entrainment app. Use my code to get 5 bonus premium days:';

const ReferralPanel = ({ onClose }) => {
  const { theme } = useTheme();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    fetchReferralInfo();
  }, []);

  const fetchReferralInfo = async () => {
    try {
      const resp = await fetch(`${API}/referral/info`, { credentials: 'include' });
      if (!resp.ok) throw new Error();
      setInfo(await resp.json());
    } catch {
      toast({ title: 'Error', description: 'Failed to load referral info', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (!info) return;
    navigator.clipboard.writeText(info.code);
    setCopied(true);
    toast({ title: 'Copied!', description: 'Referral code copied to clipboard' });
    setTimeout(() => setCopied(false), 2000);
  };

  const referralUrl = info ? `${window.location.origin}/app?ref=${info.code}` : '';
  const shareMessage = info ? `${SHARE_TEXT_SHORT} ${info.code} — ${referralUrl}` : '';

  const copyLink = () => {
    if (!info) return;
    navigator.clipboard.writeText(referralUrl);
    setCopiedLink(true);
    toast({ title: 'Link Copied!', description: 'Referral link copied to clipboard' });
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const shareNative = () => {
    if (!info) return;
    if (navigator.share) {
      navigator.share({
        title: 'Join NeuroWave One',
        text: shareMessage,
        url: referralUrl,
      }).catch(() => { /* user-cancelled */ });
    } else {
      copyLink();
    }
  };

  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`;
    window.open(url, '_blank', 'noopener');
  };

  const shareToWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(url, '_blank', 'noopener');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('I think you\'d like this — NeuroWave One');
    const body = encodeURIComponent(
      `Hey,\n\n${SHARE_TEXT_SHORT} ${info.code}\n\n` +
      `Open with this link: ${referralUrl}\n\n` +
      `It's a brainwave entrainment app with honest, cited protocols (no pseudoscience). ` +
      `Using my code, we both get 5 bonus premium days. Give it a try!`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const maxReached = info && info.successful_referrals >= info.max_referrals;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="referral-panel">
      <Card className="w-full max-w-md bg-gradient-to-br from-slate-900 via-purple-950/50 to-slate-900 border-purple-500/20 overflow-hidden">
        <div className="relative">
          <Button onClick={onClose} variant="ghost" size="sm" className="absolute top-3 right-3 text-white hover:bg-white/10 z-10" data-testid="referral-close-btn">
            <X className="w-5 h-5" />
          </Button>

          <div className="text-center pt-6 pb-3 px-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center mx-auto mb-3">
              <Gift className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-white text-xl font-bold mb-1" data-testid="referral-title">Refer & Earn</h2>
            <p className="text-white/50 text-sm">Share with friends, both get 5 bonus days</p>
          </div>
        </div>

        <CardContent className="px-6 pb-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : info ? (
            <>
              {/* Referral Code */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
                <p className="text-white/40 text-xs mb-2 uppercase tracking-wide">Your Referral Code</p>
                <div className="flex items-center gap-2">
                  <span className="text-white text-xl font-mono font-bold tracking-wider flex-1" data-testid="referral-code">{info.code}</span>
                  <Button onClick={copyCode} variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 shrink-0" data-testid="referral-copy-btn">
                    {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Share Buttons — per-channel + native */}
              <div className="mb-4">
                <p className="text-white/40 text-xs uppercase tracking-wide mb-2">Share via</p>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  <button
                    onClick={shareToTwitter}
                    className="flex flex-col items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg py-3 transition"
                    data-testid="referral-share-twitter"
                    title="Share on X / Twitter"
                  >
                    <Twitter className="w-4 h-4 text-cyan-300" />
                    <span className="text-[10px] text-white/60">X</span>
                  </button>
                  <button
                    onClick={shareToWhatsApp}
                    className="flex flex-col items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg py-3 transition"
                    data-testid="referral-share-whatsapp"
                    title="Share on WhatsApp"
                  >
                    <MessageCircle className="w-4 h-4 text-emerald-300" />
                    <span className="text-[10px] text-white/60">WhatsApp</span>
                  </button>
                  <button
                    onClick={shareViaEmail}
                    className="flex flex-col items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg py-3 transition"
                    data-testid="referral-share-email"
                    title="Share via email"
                  >
                    <Mail className="w-4 h-4 text-purple-300" />
                    <span className="text-[10px] text-white/60">Email</span>
                  </button>
                  <button
                    onClick={copyLink}
                    className="flex flex-col items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg py-3 transition"
                    data-testid="referral-share-copy"
                    title="Copy link"
                  >
                    {copiedLink ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-white/70" />}
                    <span className="text-[10px] text-white/60">{copiedLink ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>
                <Button
                  onClick={shareNative}
                  className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white py-4 rounded-xl font-semibold"
                  data-testid="referral-share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  More options…
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <Users className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                  <p className="text-white text-lg font-bold" data-testid="referral-count">{info.successful_referrals}</p>
                  <p className="text-white/40 text-xs">Referrals</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <Clock className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
                  <p className="text-white text-lg font-bold" data-testid="referral-bonus-days">{info.bonus_days_earned}</p>
                  <p className="text-white/40 text-xs">Bonus Days</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <Gift className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                  <p className="text-white text-lg font-bold">{info.max_referrals - info.successful_referrals}</p>
                  <p className="text-white/40 text-xs">Remaining</p>
                </div>
              </div>

              {/* Progress */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-white/40 mb-1">
                  <span>{info.successful_referrals} of {info.max_referrals} referrals</span>
                  <span>{info.bonus_days_earned}/{info.max_referrals * info.bonus_days_per_referral} days</span>
                </div>
                <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full transition-all"
                    style={{ width: `${(info.successful_referrals / info.max_referrals) * 100}%` }}
                  />
                </div>
              </div>

              {maxReached && (
                <p className="text-center text-emerald-400 text-sm font-medium">
                  You've maxed out referrals — 25 bonus days earned!
                </p>
              )}

              {/* Referred Users */}
              {info.referred_users.length > 0 && (
                <div className="mt-3">
                  <p className="text-white/40 text-xs uppercase tracking-wide mb-2">Your Referrals</p>
                  <div className="space-y-1.5">
                    {info.referred_users.map((u, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-white/60">
                        <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center text-xs text-purple-300">
                          {(u.name || '?')[0].toUpperCase()}
                        </div>
                        <span>{u.name || 'Friend'}</span>
                        <span className="ml-auto text-xs text-emerald-400">+{info.bonus_days_per_referral}d</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-white/40 text-center py-4">Could not load referral info</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralPanel;
